import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components';
import { ApiKeyRow } from '@/services/api/apiKeys';

interface RevokeApiKeyModalProps {
  row: ApiKeyRow | null;
  onClose: () => void;
  onConfirm: (row: ApiKeyRow) => Promise<void>;
}

/** Revocation is immediate and irreversible — this confirm spells that out. */
const RevokeApiKeyModal: React.FC<RevokeApiKeyModalProps> = ({
  row,
  onClose,
  onConfirm,
}) => {
  const [busy, setBusy] = useState(false);
  if (!row) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm(row);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-bg-strong-950/40"
        aria-hidden="true"
        onClick={busy ? undefined : onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-xl bg-bg-white-0 px-6 py-5 shadow-card-lg">
          <div className="flex items-center gap-2">
            <i className="ri-forbid-line text-lg text-error-base" />
            <h2 className="text-lg font-semibold text-text-strong-950">
              Revoke this key?
            </h2>
          </div>
          <p className="mt-2 text-sm text-text-sub-600">
            <span className="font-mono text-text-strong-950">
              {row.keyPrefix}…
            </span>{' '}
            ({row.name}) will stop working on its very next request. This
            cannot be undone — any system still using it will start receiving{' '}
            <code className="font-mono">401 Unauthorized</code>.
          </p>
          <p className="mt-2 text-xs text-text-soft-400">
            Rotating? Create the replacement key first, switch your systems to
            it, then revoke this one — zero downtime.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="white" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirm} disabled={busy}>
              {busy ? 'Revoking…' : 'Revoke key'}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default RevokeApiKeyModal;
